import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '../components/account/AccountLayout';
import { useAccountCounts } from '../components/account/useAccountCounts';
import { attentionItems } from '../components/account/accountData';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import './Profile.css';

interface UserStats {
  number_of_competitions?: number;
  competitions_won?: number;
  number_of_submissions?: number;
  best_ranking?: number | null;
}

const Profile: React.FC = () => {
  const { data, loading } = useAccountCounts();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [statsError, setStatsError] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsError(false);
    try {
      const res = await apiService.post('/competitions/user_stats', {}, {
        headers: { Authorization: `Bearer ${authUtils.getStoredToken()}` },
      });
      setStats(res.data);
    } catch {
      setStatsError(true);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const items = attentionItems(data, new Date());
  const tiles = stats ? [
    { label: 'Joined', value: stats.number_of_competitions ?? 0 },
    { label: 'Won', value: stats.competitions_won ?? 0 },
    { label: 'Submissions', value: stats.number_of_submissions ?? 0 },
    { label: 'Best rank', value: stats.best_ranking ? stats.best_ranking : '—' },
  ] : [];

  return (
    <AccountLayout title="Overview" data={data}>
      <div aria-live="polite">
        {statsError ? (
          <p className="account-overview__error">
            Couldn't load stats. <button type="button" className="account-overview__retry" onClick={loadStats}>Retry</button>
          </p>
        ) : !stats ? (
          <LoadingSpinner size="sm" />
        ) : (
          <div className="account-overview__stats" data-tour="account-stats">
            {tiles.map((t) => (
              <div key={t.label} className="account-overview__stat">
                <span>{t.label}</span>
                <strong>{t.value}</strong>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="account-overview__heading">Needs your attention</h2>
      {loading ? (
        <LoadingSpinner size="sm" />
      ) : items.length === 0 ? (
        <p className="account-overview__empty" data-tour="account-todo">You're all caught up.</p>
      ) : (
        <ul className="account-overview__todo" data-tour="account-todo">
          {items.map((item) => (
            <li key={item.key}><Link to={item.to}>{item.text}</Link></li>
          ))}
        </ul>
      )}
    </AccountLayout>
  );
};

export default Profile;
